import React from 'react';
import { Card, Table, Form, Badge, Spinner, Alert } from 'react-bootstrap';
import { FaShieldAlt } from 'react-icons/fa';
import { useGetRolesWithPermissions } from '../../../hooks/useGetRolesWithPermissions';
import { useUpdateRolePermissions } from '../../../hooks/useUpdateRolePermissions';
import { useRemoveRolePermissions } from '../../../hooks/useRemoveRolePermissions';

const PermissionsTab = () => {
    const [permissionStates, setPermissionStates] = React.useState({});
    
    // Fetch roles avec leurs permissions
    const { data: rolesData, isLoading, isError } = useGetRolesWithPermissions();
    const updatePermissionsMutation = useUpdateRolePermissions();
    const removePermissionsMutation = useRemoveRolePermissions();

    // Extraire les rôles de la réponse API
    const roles = React.useMemo(() => rolesData?.data || [], [rolesData]);
    
    // Extraire toutes les permissions uniques de tous les rôles
    const allPermissions = React.useMemo(() => {
        const permissionsMap = new Map();
        roles.forEach(role => {
            role.permissions?.forEach(perm => {
                if (!permissionsMap.has(perm.id)) {
                    permissionsMap.set(perm.id, {
                        id: perm.id,
                        nom: perm.name,
                        categorie: perm.name.split('_')[1] || 'Autres' // Extract category from permission name
                    });
                }
            });
        });
        return Array.from(permissionsMap.values());
    }, [roles]);

    // Fonction pour déterminer la couleur du badge selon le rôle
    const getRoleColor = (roleName) => {
        const name = roleName?.toLowerCase();
        if (name?.includes('admin')) return 'danger';
        if (name?.includes('manager')) return 'warning';
        if (name?.includes('technicien')) return 'info';
        if (name?.includes('support')) return 'secondary';
        if (name?.includes('client')) return 'success';
        if (name?.includes('banni')) return 'dark';
        return 'primary';
    };

    // Grouper les permissions par catégorie
    const permissionsByCategory = allPermissions.reduce((acc, perm) => {
        const category = perm.categorie || 'Autres';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(perm);
        return acc;
    }, {});

    // Initialiser les permissions depuis l'API
    React.useEffect(() => {
        if (roles.length > 0 && allPermissions.length > 0) {
            const initialPermissions = {};
            roles.forEach(role => {
                const rolePermissionIds = role.permissions?.map(p => p.id) || [];
                allPermissions.forEach(perm => {
                    const key = `${perm.id}-${role.id}`;
                    initialPermissions[key] = rolePermissionIds.includes(perm.id);
                });
            });
            setPermissionStates(initialPermissions);
        }
    }, [roles, allPermissions]);

    // Gérer le changement de permission
    const handlePermissionChange = (permissionId, roleId) => {
    const key = `${permissionId}-${roleId}`;
    const isCurrentlyChecked = permissionStates[key] || false;

    setPermissionStates(prev => ({ ...prev, [key]: !isCurrentlyChecked }));

    if (isCurrentlyChecked) {
        // était coché → on retire
        removePermissionsMutation.mutate({ roleId, permissionIds: [permissionId] });
    } else {
        // était décoché → on ajoute
        updatePermissionsMutation.mutate({ roleId, permissionIds: [permissionId] });
    }
};

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" style={{ color: "#4e73df" }}>
                    <span className="visually-hidden">Chargement...</span>
                </Spinner>
            </div>
        );
    }

    if (isError) {
        return (
            <Alert variant="danger">
                Erreur lors du chargement des permissions
            </Alert>
        );
    }

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">Gestion des permissions</h5>
            </div>

            <Card className="shadow mb-4">
                <Card.Header className="py-3 d-flex align-items-center">
                    <FaShieldAlt className="me-2" size={20} />
                    <h6 className="m-0 fw-bold">Matrice des permissions par rôle</h6>
                </Card.Header>
                <Card.Body>
                    <p className="text-muted mb-4">
                        Gérez les permissions attribuées à chaque rôle. Cochez les cases pour activer ou désactiver une permission pour un rôle spécifique.
                    </p>

                    <div className="table-responsive">
                        <Table size="sm" className="mb-0">
                            <thead className="bg-body-secondary">
                                <tr>
                                    <th style={{ width: '250px' }} className="fw-bold">Permission</th>
                                    {roles.map((role) => (
                                        <th key={role.id} className="text-center" style={{ minWidth: '120px' }}>
                                            <Badge bg={getRoleColor(role.name)} className="mb-2 px-3 py-2">
                                                {role.name}
                                            </Badge>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(permissionsByCategory).sort(([a], [b]) => a.localeCompare(b)).map(([category, permissions]) => (
                                    <React.Fragment key={category}>
                                        <tr className="table-active">
                                            <td colSpan={roles.length + 1} className="fw-bold py-2">
                                                📋 {category}
                                            </td>
                                        </tr>
                                        {permissions.map((permission, index) => (
                                            <tr key={permission.id}>
                                                <td className="py-2">
                                                    <code className="text-body">
                                                        {permission.nom}
                                                    </code>
                                                </td>
                                                {roles.map((role) => {
                                                    const key = `${permission.id}-${role.id}`;
                                                    const isChecked = permissionStates[key] || false;
                                                    const roleColor = getRoleColor(role.name);
                                                    
                                                    // Déterminer la classe de background selon le rôle et l'état
                                                    let bgClass = '';
                                                    if (isChecked) {
                                                        if (roleColor === 'danger') bgClass = 'bg-danger bg-opacity-10';
                                                        else if (roleColor === 'warning') bgClass = 'bg-warning bg-opacity-10';
                                                        else if (roleColor === 'info') bgClass = 'bg-info bg-opacity-10';
                                                        else if (roleColor === 'success') bgClass = 'bg-success bg-opacity-10';
                                                        else bgClass = 'bg-light bg-opacity-50';
                                                    }
                                                    
                                                    return (
                                                        <td 
                                                            key={role.id} 
                                                            className={`text-center py-2 ${bgClass}`}
                                                        >
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={`perm-${permission.id}-role-${role.id}`}
                                                                checked={isChecked}
                                                                onChange={() => handlePermissionChange(permission.id, role.id)}
                                                                disabled={updatePermissionsMutation.isLoading}
                                                                className="d-inline-block"
                                                                style={{ 
                                                                    cursor: updatePermissionsMutation.isLoading ? 'wait' : 'pointer',
                                                                    transform: 'scale(1.2)'
                                                                }}
                                                            />
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    <div className="alert alert-primary mt-4 d-flex align-items-center" role="alert">
                        <FaShieldAlt className="me-2" />
                        <div>
                            <strong>Note :</strong> Les modifications des permissions sont appliquées immédiatement. Les utilisateurs devront se reconnecter pour que les changements prennent effet.
                        </div>
                    </div>
                </Card.Body>
            </Card>
        </>
    );
};

export default PermissionsTab;
